import { FC, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';
import { useGetLeads } from 'modules/account/contact/hooks';
import { useGetProfile } from 'modules/account/settings/hooks';
import { IoMdClose } from 'react-icons/io';
import { PeopleIcon } from 'ui/components/icon';
import { Avatar } from 'ui/components/img';
import { SIZE } from 'utils/constants';
import { validateEmailAddress } from 'utils/helpers';

import { EmailInviteAutocomplete } from '../../components';
import { EVENT_FORM } from '../schema';

const AddGuest: FC = () => {
  const { setFieldValue, values, errors, isSubmitting } =
    useFormikContext<EVENT_FORM>();
  const [isEditing, setIsEditing] = useState(false);
  const [contactsSearch, setContactsSearch] = useState('');
  const { data: leads } = useGetLeads({ keyword: contactsSearch });
  const { data: profile } = useGetProfile();

  const guestLists = useMemo(() => {
    const list =
      leads?.data
        ?.filter(
          (lead) =>
            !values.guests
              .map((g) => g.emailAddress)
              .includes(lead.emailAddress),
        )
        .map((lead) => ({
          label: (
            <div className="flex items-center gap-x-3">
              <Avatar user={lead} size={SIZE.Small} />
              <div>
                <p className="text-sm text-left">{lead.fullName}</p>
                <p className="text-content text-xs text-left">
                  {lead.emailAddress}
                </p>
              </div>
            </div>
          ),
          value: {
            _id: lead._id,
            emailAddress: lead.emailAddress,
            firstName: lead.firstName,
            lastName: lead.lastName,
            phoneNumber: lead.mobileNumber,
          },
        })) ?? [];

    if (!list.length && validateEmailAddress(contactsSearch)) {
      return [
        {
          label: <p className="text-sm">{contactsSearch}</p>,
          value: { emailAddress: contactsSearch },
        },
      ];
    }

    return list;
  }, [leads?.data, values.guests, contactsSearch]);

  return (
    <div>
      <div className="flex items-center py-3">
        <PeopleIcon className="mr-4 h-5 w-5 flex-shrink-0 text-content" />
        {isEditing ? (
          <EmailInviteAutocomplete
            options={guestLists}
            onChange={(query) => setContactsSearch(query)}
            onClickClear={() => setContactsSearch('')}
            onClickItem={(val) => {
              setFieldValue('guests', [...values.guests, val.value]);
              setIsEditing(false);
            }}
          />
        ) : (
          <div>
            <button
              type="button"
              className="h-9 items-center hover:text-secondary text-sm"
              onClick={() => !isSubmitting && setIsEditing(true)}
            >
              Add Guest
            </button>
            {errors?.guests && (
              <p className="text-danger-base text-sm">
                {errors.guests as string}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="ml-9 space-y-2">
        <div className="flex items-center gap-x-4">
          <Avatar user={profile} size={SIZE.Small} />
          <div>
            <p className="text-sm">{profile?.fullName}</p>
            <p className="text-content text-xs">Organizer</p>
          </div>
        </div>
        {values.guests.map((guest) => (
          <div
            key={guest?.emailAddress}
            className="flex max-w-[490px] items-center justify-between"
          >
            <div className="flex items-center gap-x-4">
              <Avatar user={guest} size={SIZE.Small} />
              <p className="text-sm">{guest?.emailAddress}</p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFieldValue(
                  'guests',
                  values.guests.filter((g) => g !== guest),
                )
              }
            >
              <IoMdClose className="h-5 w-5 text-content" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddGuest;
